import { useMemo, useState } from 'react';
import { useProfile } from '../hooks/useProfile.js';
import { apiUrl } from '../lib/config.js';
import { supabase } from '../lib/supabase.js';

const tabs = ['Personal', 'Experience', 'Education', 'Skills', 'Projects', 'Certs', 'Details', 'Resume'];

const PATCHABLE_FIELDS = new Set([
  'full_name',
  'job_title',
  'city',
  'linkedin_url',
  'industry',
  'experience_years',
  'career_preferences_json',
  'available_days',
  'preferred_times',
  'areas_of_expertise',
  'resume_url',
  // Added 2026-05-21 audit — match server-side PATCHABLE in
  // packages/server/src/routes/participant.js so the inline edit
  // pencils show on every tab that the API can actually save.
  'tools',
  'company_size',
  'application_details_json',
  'education_text',
  'projects_text',
  'certifications_text',
]);

export default function ProfilePage() {
  const { user, profile, loading, error, saving, updateProfile, refreshProfile } = useProfile();
  const [activeTab, setActiveTab] = useState('Personal');
  const [editingCard, setEditingCard] = useState('');
  const [showProfileEditor, setShowProfileEditor] = useState(false);
  const [resumePreviewError, setResumePreviewError] = useState('');
  const [previewingResume, setPreviewingResume] = useState(false);

  const model = useMemo(() => buildProfileModel(profile, user), [profile, user]);

  async function saveChanges(changes) {
    await updateProfile(changes);
    setEditingCard('');
    setShowProfileEditor(false);
  }

  async function previewResume() {
    setResumePreviewError('');
    setPreviewingResume(true);

    const previewWindow = window.open('about:blank', '_blank');
    if (previewWindow) previewWindow.opener = null;

    try {
      const {
        data: { session: liveSession },
      } = await supabase.auth.getSession();
      const token = liveSession?.access_token;

      if (!token) {
        throw new Error('Your sign-in expired. Please sign in again.');
      }

      const response = await fetch(apiUrl('/api/v1/participant/me/resume/preview'), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      const payload = await response.json().catch(() => ({}));

      if (!response.ok || !payload?.url) {
        throw new Error(payload?.message || payload?.error || 'Could not preview your resume.');
      }

      if (previewWindow) {
        previewWindow.location = payload.url;
      } else {
        window.location.assign(payload.url);
      }
    } catch (err) {
      if (previewWindow) previewWindow.close();
      setResumePreviewError(err?.message || 'Could not preview your resume.');
    } finally {
      setPreviewingResume(false);
    }
  }

  if (loading) return <ProfileSkeleton />;

  if (error && !profile) {
    return (
      <StateCard
        title="Profile could not load"
        body={error}
        actionLabel="Try again"
        onAction={refreshProfile}
      />
    );
  }

  if (!profile) {
    return (
      <StateCard
        title="Finish onboarding to build your profile"
        body="Your secure profile row is not linked yet. Complete onboarding first, then this page will show your live career profile."
      />
    );
  }

  return (
    <section className="space-y-6">
      {error ? (
        <div className="rounded-xl border border-coral-200 bg-coral-50 px-4 py-3 text-sm font-bold text-coral-700 shadow-sm">
          {error}
        </div>
      ) : null}

      <ProfileHeader
        model={model}
        onEdit={() => setShowProfileEditor(true)}
      />

      <div className="grid gap-6 lg:grid-cols-[280px_minmax(0,1fr)]">
        <aside className="space-y-6 lg:sticky lg:top-6 lg:self-start">
          <MiniProfileCard model={model} />
          <ChecklistCard checklist={model.checklist} />
        </aside>

        <main className="min-w-0 space-y-6">
          <BuildFasterBanner
            missing={model.completeness.missing}
            onJump={(tab) => setActiveTab(tab)}
            onReview={() => setShowProfileEditor(true)}
          />
          <ProfileTabs activeTab={activeTab} onChange={setActiveTab} />
          <CompletenessCard
            completeness={model.completeness}
            onJump={(tab) => setActiveTab(tab)}
            onReview={() => setShowProfileEditor(true)}
          />
          {activeTab === 'Resume' ? (
            <ResumePanel
              model={model}
              error={resumePreviewError}
              previewing={previewingResume}
              onPreview={previewResume}
            />
          ) : (
            <TabPanel
              tab={activeTab}
              cards={model.tabs[activeTab] || []}
              editingCard={editingCard}
              onEdit={setEditingCard}
              onCancel={() => setEditingCard('')}
              onSave={saveChanges}
              saving={saving}
            />
          )}
        </main>
      </div>

      {showProfileEditor ? (
        <ProfileEditorModal
          model={model}
          saving={saving}
          onClose={() => setShowProfileEditor(false)}
          onSave={saveChanges}
        />
      ) : null}
    </section>
  );
}

function buildProfileModel(profile, user) {
  const fullName = clean(profile?.full_name) || clean(user?.user_metadata?.full_name) || 'Not set';
  const role = clean(profile?.latest_role) || clean(profile?.job_title) || clean(profile?.desired_role) || 'Add your role';
  const location = clean(profile?.city) || clean(profile?.location) || clean(profile?.country) || 'Add your location';
  const email = clean(user?.email) || clean(profile?.email) || 'Not set';
  const whatsapp = clean(profile?.whatsapp_number) || clean(profile?.phone) || 'Not set';
  const linkedinUrl = clean(profile?.linkedin_url);
  const resumeUrl = clean(profile?.resume_url);
  const preferences = normalizeObject(profile?.career_preferences_json);
  const applicationDetails = normalizeObject(profile?.application_details_json);
  const education = firstValue(profile?.education, profile?.education_json, profile?.education_level, profile?.school, profile?.degree);
  const areas = normalizeArray(profile?.areas_of_expertise);
  const tools = buildTools(profile);

  const checklist = [
    { label: 'Add LinkedIn', done: Boolean(linkedinUrl), tab: 'Personal' },
    { label: 'Add resume', done: Boolean(resumeUrl), tab: 'Resume' },
    { label: 'Add experience', done: Number.parseFloat(profile?.experience_years) > 0, tab: 'Experience' },
    { label: 'Add education', done: hasValue(education), tab: 'Education' },
    { label: 'Set job preferences', done: Object.keys(preferences).length > 0, tab: 'Personal' },
    { label: 'Complete first application', done: profile?.career_onboarding_step === 'complete', tab: 'Details' },
  ];

  const completenessFields = [
    { key: 'full_name', label: 'Name', tab: 'Personal', weight: 15, filled: clean(profile?.full_name) },
    { key: 'linkedin_url', label: 'LinkedIn', tab: 'Personal', weight: 15, filled: linkedinUrl },
    { key: 'resume_url', label: 'Resume', tab: 'Resume', weight: 15, filled: resumeUrl },
    { key: 'job_title', label: 'Job title', tab: 'Experience', weight: 10, filled: clean(profile?.latest_role) || clean(profile?.job_title) },
    { key: 'industry', label: 'Industry', tab: 'Experience', weight: 10, filled: clean(profile?.industry) },
    { key: 'experience_years', label: 'Experience', tab: 'Experience', weight: 10, filled: Number.parseFloat(profile?.experience_years) > 0 },
    { key: 'city', label: 'Location', tab: 'Personal', weight: 10, filled: clean(profile?.city) || clean(profile?.location) },
    { key: 'career_preferences_json', label: 'Job preferences', tab: 'Personal', weight: 15, filled: Object.keys(preferences).length > 0 },
  ];

  const completeness = completenessFields.reduce(
    (acc, item) => {
      if (item.filled) acc.percent += item.weight;
      else acc.missing.push(item);
      return acc;
    },
    { percent: 0, missing: [] },
  );

  return {
    raw: profile,
    fullName,
    initials: getInitials(fullName),
    role,
    location,
    email,
    whatsapp,
    linkedinUrl,
    linkedinStatus: linkedinUrl ? 'Connected' : 'Not connected',
    memberSince: formatMemberSince(profile?.created_at),
    resumeUrl,
    resumeName: getFileName(resumeUrl),
    preferences,
    checklist,
    completeness,
    tabs: {
      Personal: [
        {
          id: 'contact',
          title: 'Contact',
          fields: [
            { label: 'Full name', value: clean(profile?.full_name), patchKey: 'full_name', placeholder: 'Add your full name' },
            { label: 'Email', value: email, readOnly: true, help: 'Email comes from your Supabase auth account.' },
            { label: 'WhatsApp', value: whatsapp, readOnly: true, help: 'WhatsApp is managed by onboarding and messaging.' },
            { label: 'City', value: clean(profile?.city), patchKey: 'city', placeholder: 'Add your city' },
            { label: 'LinkedIn', value: linkedinUrl, patchKey: 'linkedin_url', placeholder: 'Add your LinkedIn URL' },
          ],
        },
        {
          id: 'career-target',
          title: 'Career target',
          fields: [
            { label: 'Industry', value: clean(profile?.industry), patchKey: 'industry', placeholder: 'Add your industry' },
            { label: 'Target role', value: role === 'Add your role' ? '' : role, patchKey: 'job_title', placeholder: 'Add your target role' },
            { label: 'Job preferences', value: preferences, patchKey: 'career_preferences_json', type: 'json', placeholder: 'Add job preferences as JSON' },
          ],
        },
      ],
      Experience: [
        {
          id: 'experience',
          title: role,
          meta: clean(profile?.industry) || 'Industry not set',
          fields: [
            { label: 'Current role', value: clean(profile?.current_role) || (role === 'Add your role' ? '' : role), patchKey: 'job_title', placeholder: 'Add your current role' },
            { label: 'Industry', value: clean(profile?.industry), patchKey: 'industry', placeholder: 'Add your industry' },
            { label: 'Experience years', value: clean(profile?.experience_years), patchKey: 'experience_years', inputMode: 'numeric', placeholder: 'Add years of experience' },
          ],
        },
      ],
      Education: [
        {
          id: 'education',
          title: 'Education',
          fields: [
            // Was read-only with no DB column. Migration 021 added
            // interviewees.education_text — backs free-form markdown.
            { label: 'Education', value: clean(profile?.education_text), patchKey: 'education_text', type: 'textarea', placeholder: 'e.g. BS Computer Science, MIT 2020\nMBA, Wharton 2024' },
          ],
        },
      ],
      Skills: [
        {
          id: 'skills',
          title: 'Core skills',
          fields: [
            { label: 'Areas of expertise', value: areas, patchKey: 'areas_of_expertise', type: 'array', placeholder: 'Add skills separated by commas' },
            // tools (text[]) IS in PATCHABLE; the composite "Tools" we
            // used to show was a derived view of multiple legacy boolean
            // columns. Simplified to a single editable comma-list backed
            // directly by interviewees.tools.
            { label: 'Tools', value: normalizeArray(profile?.tools), patchKey: 'tools', type: 'array', placeholder: 'Figma, Notion, Linear, Python' },
          ],
        },
      ],
      Projects: [
        {
          id: 'projects',
          title: 'Projects',
          fields: [
            { label: 'Projects', value: clean(profile?.projects_text), patchKey: 'projects_text', type: 'textarea', placeholder: 'List your notable projects (one per line is fine)' },
          ],
        },
      ],
      Certs: [
        {
          id: 'certs',
          title: 'Certifications',
          fields: [
            { label: 'Certifications', value: clean(profile?.certifications_text), patchKey: 'certifications_text', type: 'textarea', placeholder: 'e.g. AWS Solutions Architect (2024), PMP (2023)' },
          ],
        },
      ],
      Details: [
        {
          id: 'details',
          title: 'Availability details',
          fields: [
            { label: 'Company size', value: clean(profile?.company_size), patchKey: 'company_size', placeholder: 'e.g. startup, 50–500, Fortune 500' },
            { label: 'Available days', value: normalizeArray(profile?.available_days || preferences.availability_days), patchKey: 'available_days', type: 'array', placeholder: 'Mon, Tue, Wed' },
            { label: 'Preferred times', value: normalizeArray(profile?.preferred_times || preferences.availability_times), patchKey: 'preferred_times', type: 'array', placeholder: 'Mornings, Afternoons' },
            { label: 'Application details', value: applicationDetails, patchKey: 'application_details_json', type: 'json', placeholder: '{"work_authorization": "US citizen", ...}' },
          ],
        },
      ],
    },
  };
}

function ProfileHeader({ model, onEdit }) {
  return (
    <header className="overflow-hidden rounded-xl bg-white shadow-lg shadow-slate-900/5">
      <div className="bg-gradient-to-br from-teal-600 via-teal-700 to-obsidian-400 px-5 pb-16 pt-7 text-white sm:px-7">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="min-w-0">
            <h1 className="break-words text-4xl leading-none text-white sm:text-5xl">{model.fullName}</h1>
            <p className="mt-2 text-sm font-semibold text-teal-50">{model.role}</p>
          </div>
          <span className="shrink-0 text-left text-[11px] font-semibold uppercase tracking-[0.12em] text-teal-100/80 sm:text-right">{model.memberSince}</span>
        </div>
      </div>

      <div className="-mt-10 flex flex-col gap-4 px-5 pb-5 sm:px-7 md:flex-row md:items-end md:justify-between">
        <div className="flex items-end gap-4">
          <div className="flex h-24 w-24 shrink-0 items-center justify-center rounded-full border-4 border-white bg-teal-100 font-display text-4xl text-teal-900 shadow-lg shadow-teal-900/10">
            {model.initials}
          </div>
          <div className="flex flex-wrap gap-2 pb-2">
            {model.linkedinUrl ? <Badge tone="teal" label="LinkedIn connected" icon={<LinkedInIcon className="h-3.5 w-3.5" />} /> : null}
            <Badge tone="slate" label={model.location} />
          </div>
        </div>

        <button type="button" onClick={onEdit} className="inline-flex h-11 cursor-pointer items-center justify-center gap-2 rounded-[8px] bg-obsidian-400 px-4 text-sm font-bold text-white shadow-lg shadow-slate-900/15 transition hover:-translate-y-0.5 hover:bg-slate-900 focus:outline-none focus:ring-4 focus:ring-teal-200">
          <EditIcon className="h-4 w-4" />
          Edit profile
        </button>
      </div>
    </header>
  );
}

function MiniProfileCard({ model }) {
  return (
    <section className="rounded-xl bg-slate-50 p-4 shadow-sm shadow-slate-900/5">
      <div className="flex items-center gap-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-teal-100 font-display text-xl text-teal-900">{model.initials}</div>
        <div className="min-w-0">
          <p className="truncate font-bold text-obsidian-400">{model.fullName}</p>
          <p className="truncate text-sm text-slate-500">{model.role}</p>
        </div>
      </div>
      <div className="mt-4 space-y-3 text-sm">
        <InfoRow label="Email" value={model.email} />
        <InfoRow label="WhatsApp" value={model.whatsapp} />
        <InfoRow label="LinkedIn" value={model.linkedinStatus} />
      </div>
    </section>
  );
}

function ChecklistCard({ checklist }) {
  const complete = checklist.filter((item) => item.done).length;
  const percent = Math.round((complete / checklist.length) * 100);

  return (
    <section className="rounded-xl bg-white p-4 shadow-sm shadow-slate-900/5">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-bold leading-none text-obsidian-400">Progress</h2>
          <p className="mt-1 text-sm font-bold text-slate-500">{complete} of {checklist.length} steps complete</p>
        </div>
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-emerald-50 text-sm font-bold text-emerald-700">{percent}%</div>
      </div>
      <div className="mt-4 h-2 overflow-hidden rounded-full bg-slate-100">
        <div className="h-full rounded-full bg-emerald-600" style={{ width: `${percent}%` }} />
      </div>
      <ul className="mt-4 space-y-3">
        {checklist.map((item) => (
          <li key={item.label} className="flex items-center gap-3 text-sm">
            <span className={['flex h-6 w-6 items-center justify-center rounded-full', item.done ? 'bg-emerald-600 text-white' : 'bg-slate-100 text-slate-400'].join(' ')}>
              {item.done ? <CheckIcon className="h-3.5 w-3.5" /> : <span className="h-2 w-2 rounded-full bg-current" />}
            </span>
            <span className={item.done ? 'font-bold text-slate-800' : 'text-slate-500'}>{item.label}</span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function BuildFasterBanner({ missing, onJump, onReview }) {
  const primary = missing[0];
  const handleClick = () => {
    if (primary) onJump(primary.tab || 'Resume');
    else onReview();
  };

  return (
    <section className="overflow-hidden rounded-xl bg-[#F0FDFA] p-5 shadow-sm shadow-teal-900/5 sm:flex sm:items-center sm:justify-between sm:gap-6">
      <div>
        <p className="text-xs font-bold uppercase tracking-[0.16em] text-teal-700">Build your profile faster</p>
        <h2 className="mt-2 text-xl font-bold leading-tight text-obsidian-400">Pull in your LinkedIn or paste your resume.</h2>
        <p className="mt-2 text-sm leading-6 text-slate-600">Pareto can extract experience, skills, and preferences before your next match run.</p>
      </div>
      <button
        type="button"
        onClick={handleClick}
        className="mt-4 inline-flex h-11 w-full cursor-pointer items-center justify-center rounded-[8px] bg-[#0D9488] px-4 text-sm font-bold text-white shadow-lg shadow-teal-900/10 transition hover:-translate-y-0.5 hover:bg-teal-700 focus:outline-none focus:ring-4 focus:ring-teal-200 sm:mt-0 sm:w-auto"
      >
        {primary ? `Add ${primary.label}` : 'Review profile'}
      </button>
    </section>
  );
}

function ProfileTabs({ activeTab, onChange }) {
  return (
    <div className="-mx-4 overflow-x-auto px-4 sm:mx-0 sm:px-0" role="tablist" aria-label="Profile sections">
      <div className="flex min-w-max gap-2 rounded-xl bg-slate-100 p-1 shadow-sm shadow-slate-900/5">
        {tabs.map((tab) => (
          <button
            key={tab}
            type="button"
            role="tab"
            aria-selected={activeTab === tab}
            onClick={() => onChange(tab)}
            className={[
              'h-10 cursor-pointer rounded-[8px] px-4 text-sm font-bold transition focus:outline-none focus:ring-4 focus:ring-teal-100',
              activeTab === tab ? 'bg-[#0D1117] text-white shadow-sm' : 'bg-transparent text-slate-500 hover:bg-white hover:text-slate-800',
            ].join(' ')}
          >
            {tab}
          </button>
        ))}
      </div>
    </div>
  );
}

function CompletenessCard({ completeness, onJump, onReview }) {
  const actions = completeness.missing.length ? completeness.missing : [{ label: 'Review', tab: 'Personal', review: true }];

  return (
    <section className="rounded-xl bg-[#FEF3C7] p-5 shadow-sm shadow-amber-900/5">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-xl font-bold leading-tight text-obsidian-400">Profile {completeness.percent}% Complete</h2>
          <p className="mt-2 text-sm font-bold text-amber-800">
            {completeness.missing.length === 0 ? '0 recommended fields missing.' : `${completeness.missing.length} recommended fields missing.`}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          {actions.slice(0, 4).map((item) => (
            <button key={item.label} type="button" onClick={() => (item.review ? onReview() : onJump(item.tab))} className="inline-flex cursor-pointer items-center gap-1.5 rounded-full bg-white px-3 py-1.5 text-xs font-bold text-teal-800 shadow-sm transition hover:-translate-y-0.5 hover:bg-teal-50 hover:shadow-md focus:outline-none focus:ring-4 focus:ring-teal-100">
              {item.label}
              <ArrowRightIcon className="h-3 w-3" />
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}

function TabPanel({ cards, editingCard, onEdit, onCancel, onSave, saving }) {
  return (
    <section className="grid gap-6 md:grid-cols-2" role="tabpanel">
      {cards.map((card) => (
        <ProfileDataCard
          key={card.id}
          card={card}
          editing={editingCard === card.id}
          onEdit={() => onEdit(card.id)}
          onCancel={onCancel}
          onSave={onSave}
          saving={saving}
        />
      ))}
    </section>
  );
}

function ProfileDataCard({ card, editing, onEdit, onCancel, onSave, saving }) {
  const editableFields = card.fields.filter((field) => field.patchKey && PATCHABLE_FIELDS.has(field.patchKey));
  const [draft, setDraft] = useState(() => fieldsToDraft(card.fields));
  const [localError, setLocalError] = useState('');

  function startEdit() {
    setDraft(fieldsToDraft(card.fields));
    setLocalError('');
    onEdit();
  }

  async function save() {
    setLocalError('');
    let changes;
    try {
      changes = {};
      for (const field of editableFields) {
        changes[field.patchKey] = draftValueToPayload(field, draft[field.patchKey]);
      }
    } catch (err) {
      // draftValueToPayload throws on invalid JSON. Surface inline so
      // the user sees why the click did nothing.
      setLocalError(err?.message || 'Could not save your changes.');
      return;
    }

    try {
      await onSave(changes);
    } catch (err) {
      setLocalError(err?.message || 'Could not save your changes.');
    }
  }

  return (
    <article className="relative rounded-xl bg-white p-5 pr-16 shadow-sm shadow-slate-900/5 transition hover:-translate-y-0.5 hover:shadow-lg">
      <div>
        <h3 className="text-lg font-bold leading-tight text-obsidian-400">{card.title}</h3>
        {card.meta ? <p className="mt-2 text-sm font-bold text-slate-500">{card.meta}</p> : null}
      </div>

      {editableFields.length ? <IconButton label={`Edit ${card.title}`} onClick={startEdit} /> : null}

      <div className="mt-4 space-y-3">
        {card.fields.map((field) => (
          <EditableField
            key={`${card.id}-${field.label}`}
            field={field}
            editing={editing && field.patchKey && PATCHABLE_FIELDS.has(field.patchKey)}
            value={draft[field.patchKey]}
            onChange={(value) => setDraft((current) => ({ ...current, [field.patchKey]: value }))}
          />
        ))}
      </div>

      {editing ? (
        <div className="mt-5 space-y-3">
          {localError ? (
            <div className="rounded-[8px] border border-coral-200 bg-coral-50 px-3 py-2 text-sm font-bold text-coral-700">
              {localError}
            </div>
          ) : null}
          <div className="flex flex-col gap-2 sm:flex-row">
            <button type="button" onClick={save} disabled={saving} className="inline-flex h-10 cursor-pointer items-center justify-center rounded-[8px] bg-teal-600 px-4 text-sm font-bold text-white shadow-sm transition hover:bg-teal-700 focus:outline-none focus:ring-4 focus:ring-teal-100 disabled:cursor-not-allowed disabled:opacity-60">
              {saving ? 'Saving...' : 'Save changes'}
            </button>
            <button type="button" onClick={() => { setLocalError(''); onCancel(); }} disabled={saving} className="inline-flex h-10 cursor-pointer items-center justify-center rounded-[8px] bg-slate-50 px-4 text-sm font-bold text-slate-700 shadow-sm transition hover:bg-slate-100 focus:outline-none focus:ring-4 focus:ring-slate-100 disabled:cursor-not-allowed disabled:opacity-60">
              Cancel
            </button>
          </div>
        </div>
      ) : null}
    </article>
  );
}

function EditableField({ field, editing, value, onChange }) {
  if (editing) {
    // Always pass a string so the input/textarea stays controlled —
    // toggling between undefined and a string makes React drop onChange
    // updates and the save then sends stale data.
    const controlledValue = value == null ? '' : value;
    if (field.type === 'json' || field.type === 'textarea') {
      return (
        <label className="block">
          <span className="text-xs font-bold uppercase tracking-[0.12em] text-slate-400">{field.label}</span>
          <textarea
            value={controlledValue}
            onChange={(event) => onChange(event.target.value)}
            placeholder={field.placeholder}
            rows={field.type === 'json' ? 5 : 4}
            className="mt-1 w-full rounded-[8px] border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 shadow-sm outline-none transition focus:border-teal-500 focus:ring-4 focus:ring-teal-100"
          />
        </label>
      );
    }

    return (
      <label className="block">
        <span className="text-xs font-bold uppercase tracking-[0.12em] text-slate-400">{field.label}</span>
        <input
          value={controlledValue}
          inputMode={field.inputMode}
          onChange={(event) => onChange(event.target.value)}
          placeholder={field.placeholder}
          className="mt-1 h-10 w-full rounded-[8px] border border-slate-200 bg-white px-3 text-sm font-bold text-slate-800 shadow-sm outline-none transition focus:border-teal-500 focus:ring-4 focus:ring-teal-100"
        />
      </label>
    );
  }

  return (
    <div className="rounded-[8px] bg-slate-50 px-3 py-3 shadow-sm shadow-slate-900/5">
      <div className="flex items-center justify-between gap-3">
        <span className="text-xs font-bold uppercase tracking-[0.12em] text-slate-400">{field.label}</span>
        {field.readOnly ? <span className="text-[11px] font-bold text-slate-400">{field.help ? 'Managed' : 'Read only'}</span> : null}
      </div>
      <FieldValue field={field} />
    </div>
  );
}

function FieldValue({ field }) {
  if (field.type === 'tags' || field.type === 'array') {
    const items = normalizeArray(field.value);
    if (!items.length) return <p className="mt-2 text-sm font-bold text-slate-500">{field.placeholder || 'Not set'}</p>;
    return (
      <div className="mt-2 flex flex-wrap gap-2">
        {items.map((item) => <Tag key={item}>{item}</Tag>)}
      </div>
    );
  }

  if (field.type === 'json') {
    const obj = normalizeObject(field.value);
    if (!Object.keys(obj).length) return <p className="mt-2 text-sm font-bold text-slate-500">{field.placeholder || 'Not set'}</p>;
    return <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap rounded-[8px] bg-white p-3 text-xs leading-5 text-slate-600">{JSON.stringify(obj, null, 2)}</pre>;
  }

  if (field.type === 'textarea') {
    const text = clean(field.value);
    if (!text) return <p className="mt-2 text-sm font-bold text-slate-500">{field.placeholder || 'Not set'}</p>;
    // Preserve newlines in display mode — markdown-lite for education /
    // projects / certifications free-form text.
    return <p className="mt-2 whitespace-pre-line break-words text-sm text-slate-800">{text}</p>;
  }

  const value = formatValue(field.value);
  return <p className={['mt-2 break-words text-sm font-bold', value === 'Not set' ? 'text-slate-500' : 'text-slate-800'].join(' ')}>{value === 'Not set' ? field.placeholder || value : value}</p>;
}

function ResumePanel({ model, error, previewing, onPreview }) {
  return (
    <section className="grid gap-6 lg:grid-cols-[1fr_280px]" role="tabpanel">
      <article className="relative rounded-xl bg-white p-5 shadow-sm shadow-slate-900/5">
        <div>
          <h3 className="text-lg font-bold leading-tight text-obsidian-400">Resume workspace</h3>
          <p className="mt-2 text-sm leading-6 text-slate-600">
            {model.resumeUrl ? `Uploaded resume: ${model.resumeName}` : 'No resume uploaded yet'}
          </p>
        </div>
        {error ? (
          <div className="mt-4 rounded-[8px] border border-coral-200 bg-coral-50 px-3 py-2 text-sm font-bold text-coral-700">
            {error}
          </div>
        ) : null}
        <div className="mt-5 grid gap-3 sm:grid-cols-2">
          {model.resumeUrl ? (
            <button type="button" onClick={onPreview} disabled={previewing} className="inline-flex h-12 cursor-pointer items-center justify-center gap-2 rounded-[8px] bg-teal-600 px-4 text-sm font-bold text-white shadow-lg shadow-slate-900/10 transition hover:-translate-y-0.5 hover:bg-teal-700 focus:outline-none focus:ring-4 focus:ring-teal-200 disabled:cursor-not-allowed disabled:opacity-70">
              <DocumentIcon className="h-4 w-4" />
              {previewing ? 'Opening...' : 'Preview Resume'}
            </button>
          ) : (
            <button type="button" disabled className="inline-flex h-12 cursor-not-allowed items-center justify-center gap-2 rounded-[8px] bg-slate-100 px-4 text-sm font-bold text-slate-400">
              <DocumentIcon className="h-4 w-4" />
              Preview Resume
            </button>
          )}
          <button type="button" disabled className="inline-flex h-12 cursor-not-allowed items-center justify-center gap-2 rounded-[8px] bg-obsidian-400/40 px-4 text-sm font-bold text-white shadow-lg shadow-slate-900/10" title="Cover letter preview coming soon">
            <MailIcon className="h-4 w-4" />
            Preview Cover Letter
          </button>
        </div>
        <div className="mt-5 rounded-xl bg-slate-50 p-5 shadow-inner shadow-slate-900/5">
          <p className="text-sm font-bold text-slate-800">Upload a newer resume</p>
          <p className="mt-2 text-sm leading-6 text-slate-500">PDF, DOC, or DOCX. Pareto keeps the latest version for verified auto-apply runs.</p>
          <button type="button" disabled title="Resume upload coming soon" data-endpoint="/api/v1/participant/me/resume" className="mt-4 inline-flex h-10 cursor-not-allowed items-center justify-center rounded-[8px] bg-white px-4 text-sm font-bold text-slate-400 shadow-sm">
            Upload file
          </button>
        </div>
      </article>

      <article className="rounded-xl bg-white p-5 shadow-sm shadow-slate-900/5">
        <h3 className="text-lg font-bold leading-tight text-obsidian-400">Files</h3>
        <div className="mt-4 space-y-3">
          {model.resumeUrl ? <FileRow label={model.resumeName} size="Current resume" /> : <EmptyFileRow label="No resume uploaded yet" />}
          <EmptyFileRow label="Optimized resume not generated yet" />
          <EmptyFileRow label="Application packet not generated yet" />
        </div>
      </article>
    </section>
  );
}

function ProfileEditorModal({ model, saving, onClose, onSave }) {
  const [draft, setDraft] = useState({
    full_name: model.fullName === 'Not set' ? '' : model.fullName,
    job_title: model.role === 'Add your role' ? '' : model.role,
    city: model.location === 'Add your location' ? '' : model.raw?.city || '',
    linkedin_url: model.linkedinUrl || '',
    industry: model.raw?.industry || '',
    experience_years: model.raw?.experience_years || '',
  });

  async function save() {
    await onSave(draft);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end bg-slate-900/40 px-4 py-4 backdrop-blur-sm sm:items-center sm:justify-center">
      <div className="w-full max-w-lg rounded-xl bg-white p-5 shadow-2xl shadow-slate-900/25">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold leading-tight text-obsidian-400">Edit profile</h2>
            <p className="mt-1 text-sm text-slate-500">Changes save to your interviewee profile.</p>
          </div>
          <button type="button" onClick={onClose} className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-[8px] bg-slate-50 text-slate-500 transition hover:bg-slate-100 focus:outline-none focus:ring-4 focus:ring-slate-100">
            <CloseIcon className="h-4 w-4" />
          </button>
        </div>
        <div className="mt-5 grid gap-3 sm:grid-cols-2">
          {[
            ['full_name', 'Full name'],
            ['job_title', 'Job title'],
            ['city', 'City'],
            ['linkedin_url', 'LinkedIn URL'],
            ['industry', 'Industry'],
            ['experience_years', 'Experience years'],
          ].map(([key, label]) => (
            <label key={key} className="block">
              <span className="text-xs font-bold uppercase tracking-[0.12em] text-slate-400">{label}</span>
              <input
                value={draft[key]}
                onChange={(event) => setDraft((current) => ({ ...current, [key]: event.target.value }))}
                className="mt-1 h-10 w-full rounded-[8px] border border-slate-200 bg-white px-3 text-sm font-bold text-slate-800 shadow-sm outline-none transition focus:border-teal-500 focus:ring-4 focus:ring-teal-100"
              />
            </label>
          ))}
        </div>
        <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:justify-end">
          <button type="button" onClick={onClose} disabled={saving} className="inline-flex h-10 cursor-pointer items-center justify-center rounded-[8px] bg-slate-50 px-4 text-sm font-bold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-60">Cancel</button>
          <button type="button" onClick={save} disabled={saving} className="inline-flex h-10 cursor-pointer items-center justify-center rounded-[8px] bg-teal-600 px-4 text-sm font-bold text-white shadow-sm transition hover:bg-teal-700 focus:outline-none focus:ring-4 focus:ring-teal-100 disabled:cursor-not-allowed disabled:opacity-60">
            {saving ? 'Saving...' : 'Save profile'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ProfileSkeleton() {
  return (
    <section className="space-y-6 animate-pulse">
      <div className="h-56 rounded-xl bg-slate-200" />
      <div className="grid gap-6 lg:grid-cols-[280px_minmax(0,1fr)]">
        <aside className="space-y-6">
          <div className="h-48 rounded-xl bg-slate-200" />
          <div className="h-64 rounded-xl bg-slate-200" />
        </aside>
        <main className="space-y-6">
          <div className="h-32 rounded-xl bg-slate-200" />
          <div className="h-12 rounded-xl bg-slate-200" />
          <div className="grid gap-6 md:grid-cols-2">
            <div className="h-56 rounded-xl bg-slate-200" />
            <div className="h-56 rounded-xl bg-slate-200" />
          </div>
        </main>
      </div>
    </section>
  );
}

function StateCard({ title, body, actionLabel, onAction }) {
  return (
    <section className="rounded-xl bg-white p-6 text-center shadow-sm shadow-slate-900/5">
      <h1 className="text-2xl font-bold text-obsidian-400">{title}</h1>
      <p className="mx-auto mt-3 max-w-xl text-sm leading-6 text-slate-600">{body}</p>
      {actionLabel ? (
        <button type="button" onClick={onAction} className="mt-5 inline-flex h-10 cursor-pointer items-center justify-center rounded-[8px] bg-teal-600 px-4 text-sm font-bold text-white shadow-sm transition hover:bg-teal-700 focus:outline-none focus:ring-4 focus:ring-teal-100">
          {actionLabel}
        </button>
      ) : null}
    </section>
  );
}

function InfoRow({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-[8px] bg-white px-3 py-2 shadow-sm shadow-slate-900/5">
      <span className="text-slate-400">{label}</span>
      <span className="truncate text-right font-bold text-slate-700">{formatValue(value)}</span>
    </div>
  );
}

function IconButton({ label, onClick }) {
  return (
    <button type="button" aria-label={label} onClick={onClick} className="absolute right-4 top-4 flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-[8px] bg-slate-50 text-slate-400 shadow-sm transition hover:bg-teal-50 hover:text-teal-700 focus:outline-none focus:ring-4 focus:ring-teal-100">
      <EditIcon className="h-4 w-4" />
    </button>
  );
}

function Badge({ label, icon, tone }) {
  const colors = tone === 'teal' ? 'bg-teal-50 text-teal-800 ring-teal-200' : 'bg-slate-100 text-slate-700 ring-slate-200';
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold ring-1 ${colors}`}>
      {icon}
      {label}
    </span>
  );
}

function Tag({ children }) {
  return <span className="rounded-full bg-white px-3 py-1 text-xs font-bold text-teal-800 shadow-sm ring-1 ring-teal-100">{children}</span>;
}

function FileRow({ label, size }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-[8px] bg-slate-50 px-3 py-3 shadow-sm shadow-slate-900/5">
      <div className="min-w-0">
        <p className="truncate text-sm font-bold text-slate-800">{label}</p>
        <p className="text-xs text-slate-500">{size}</p>
      </div>
      <DocumentIcon className="h-4 w-4 shrink-0 text-slate-400" />
    </div>
  );
}

function EmptyFileRow({ label }) {
  return (
    <div className="rounded-[8px] bg-slate-50 px-3 py-3 text-sm font-bold text-slate-400 shadow-sm shadow-slate-900/5">
      {label}
    </div>
  );
}

function fieldsToDraft(fields) {
  return fields.reduce((acc, field) => {
    if (!field.patchKey) return acc;
    if (field.type === 'json') acc[field.patchKey] = JSON.stringify(normalizeObject(field.value), null, 2);
    else if (field.type === 'array') acc[field.patchKey] = normalizeArray(field.value).join(', ');
    else acc[field.patchKey] = clean(field.value);
    return acc;
  }, {});
}

function draftValueToPayload(field, value) {
  if (field.type === 'json') {
    try {
      return JSON.parse(value || '{}');
    } catch {
      throw new Error(`${field.label} must be valid JSON.`);
    }
  }
  if (field.type === 'array') return normalizeArray(value);
  return value;
}

function buildTools(profile) {
  const tools = [];
  if (profile?.tools_analytics) tools.push('Analytics');
  if (profile?.tools_project_mgmt) tools.push('Project management');
  if (profile?.tools_design) tools.push('Design');
  if (profile?.tools) tools.push(...normalizeArray(profile.tools));
  return Array.from(new Set(tools));
}

function getInitials(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length || name === 'Not set') return 'P';
  return `${parts[0]?.[0] || ''}${parts.length > 1 ? parts[parts.length - 1]?.[0] || '' : ''}`.toUpperCase();
}

function formatMemberSince(value) {
  if (!value) return 'Member since not set';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Member since not set';
  return `Member since ${date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}`;
}

function getFileName(value) {
  if (!value) return 'Resume';
  const cleanValue = String(value).split('?')[0];
  return decodeURIComponent(cleanValue.split('/').pop() || 'Resume');
}

function firstValue(...values) {
  return values.find((value) => hasValue(value));
}

function hasValue(value) {
  if (Array.isArray(value)) return value.length > 0;
  if (value && typeof value === 'object') return Object.keys(value).length > 0;
  return Boolean(clean(value));
}

function normalizeObject(value) {
  if (!value) return {};
  if (typeof value === 'object' && !Array.isArray(value)) return value;
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch {
      return {};
    }
  }
  return {};
}

function normalizeArray(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value.map(clean).filter(Boolean);
  if (typeof value === 'string') return value.split(',').map(clean).filter(Boolean);
  return [];
}

function clean(value) {
  return typeof value === 'string' ? value.trim() : value == null ? '' : String(value).trim();
}

function formatValue(value) {
  if (Array.isArray(value)) return value.length ? value.join(', ') : 'Not set';
  if (value && typeof value === 'object') return Object.keys(value).length ? JSON.stringify(value) : 'Not set';
  return clean(value) || 'Not set';
}

function ArrowRightIcon(props) {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" {...props}>
      <path d="M11.3 4.3 17 10l-5.7 5.7-1.4-1.4 3.3-3.3H3V9h10.2L9.9 5.7l1.4-1.4Z" />
    </svg>
  );
}

function CloseIcon(props) {
  return (
    <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true" {...props}>
      <path d="m5 5 10 10M15 5 5 15" />
    </svg>
  );
}

function CheckIcon(props) {
  return (
    <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" {...props}>
      <path d="M7.7 13.7 3.9 9.9l1.4-1.4 2.4 2.4 7-7 1.4 1.4-8.4 8.4Z" />
    </svg>
  );
}

function EditIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5Z" />
    </svg>
  );
}

function LinkedInIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
      <path d="M6.9 8.7H3.5V20h3.4V8.7ZM5.2 7.2a2 2 0 1 0 0-4 2 2 0 0 0 0 4ZM20.5 20h-3.4v-5.5c0-1.3 0-3-1.8-3s-2.1 1.4-2.1 2.9V20H9.8V8.7h3.3v1.5h.1c.5-.9 1.6-1.8 3.2-1.8 3.5 0 4.1 2.3 4.1 5.2V20Z" />
    </svg>
  );
}

function DocumentIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8Z" />
      <path d="M14 2v6h6" />
      <path d="M8 13h8M8 17h6" />
    </svg>
  );
}

function MailIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" {...props}>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="m3 7 9 6 9-6" />
    </svg>
  );
}
